import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { currentLocale } from '../../i18n'
import { getTableroTrabajoOt, detenerTrabajoOt } from '../../api/client'
import { useAsignarOt } from './useAsignarOt'
import { useReloj } from '../capsulas/useReloj'
import { duracion, hora, segundosVivos, enColacion } from '../capsulas/tiempo'
import { hoyLocal } from '../partes/fechas'
import { formatFecha } from '../unidades/tipos'

// Órdenes de trabajo > Asignaciones, the board -- like Mantenimiento's: the
// people who can be assigned on the left with what they're on now (an OT or
// a parte), every OT in execution on the right with who's on it. Assign by
// dragging a person onto an OT, with a person's "Asignar" or an OT's
// "+ Persona"; whatever else they were on pauses (the server does it). An OT
// without its planning asks for it first. Without "asignar_personas" it is
// only a view. People can be seen all together, by work group (a supervisor's
// view: their groups first) or only oneself (an operator signed in with their
// email); the choice is remembered in the browser.
const VISTAS_PERSONAS = ['todos', 'grupo', 'yo']
const CLAVE_VISTA = 'ot.asignar.vistaPersonas'
const leerVista = () => { try { return localStorage.getItem(CLAVE_VISTA) } catch { return null } }
export default function TableroTrabajoOt() {
  const { t } = useTranslation('operations')
  const navigate = useNavigate()
  const ahora = useReloj()
  const [datos, setDatos] = useState(null)
  const [recibido, setRecibido] = useState(0)
  const [error, setError] = useState('')
  const [buscar, setBuscar] = useState('')
  const [grupo, setGrupo] = useState('')
  const [eligiendo, setEligiendo] = useState(null)      // persona whose OT picker is open
  const [sobre, setSobre] = useState(null)               // OT id a person is dragged over
  const [vistaP, setVistaP] = useState(() => (VISTAS_PERSONAS.includes(leerVista()) ? leerVista() : null))

  const cargar = useCallback(() => {
    getTableroTrabajoOt()
      .then(d => { setDatos(d); setRecibido(Date.now()); setError('') })
      .catch(err => setError(err.response?.status === 403 ? t('ot.trabajo.sinFuncion') : t('ot.asignar.cargaFallo')))
  }, [t])
  useEffect(() => {
    cargar()
    const id = setInterval(cargar, 60000)      // catches the automatic end-of-jornada and lunch stops
    return () => clearInterval(id)
  }, [cargar])

  const { asignar, ui } = useAsignarOt({ onHecho: cargar })
  const pausar = async (x) => { try { await detenerTrabajoOt(x.id) } finally { cargar() } }

  if (error && !datos) return <p className="text-red-500 text-sm">{error}</p>
  if (!datos) return <p className="text-gray-400 text-sm">{t('ot.asignar.cargando')}</p>

  const puede = datos.puede_asignar
  const b = buscar.trim().toLowerCase()
  const grupos = [...new Set(datos.ots.map(o => o.grupo).filter(Boolean))].sort()
  const ots = datos.ots.filter(o => (!grupo || (grupo === '-' ? !o.grupo : o.grupo === grupo))
    && (!b || `${o.codigo} ${o.folio_of || ''} ${o.articulo_codigo || ''} ${o.articulo_descripcion || ''} ${o.descripcion || ''} ${o.categoria || ''} ${o.responsable || ''}`.toLowerCase().includes(b)))
  const enTablero = new Set(datos.ots.map(o => o.id))
  const trabajando = datos.personas.filter(p => p.abierta).length
  const hoy = hoyLocal()

  // Without a choice of their own: a supervisor sees their groups, someone who
  // can't assign (an operator) sees only themselves, everyone else all.
  const yoEsta = datos.personas.some(p => p.id === datos.yo)
  const modo = (vistaP === 'yo' && !datos.yo) ? 'todos'
    : vistaP || (datos.supervisa.length ? 'grupo' : !puede && yoEsta ? 'yo' : 'todos')
  const elegirVista = (v) => { setVistaP(v); try { localStorage.setItem(CLAVE_VISTA, v) } catch { /* private mode */ } }
  const secciones = modo === 'yo'
    ? [{ clave: 'yo', personas: datos.personas.filter(p => p.id === datos.yo) }]
    : modo === 'grupo'
    ? [...datos.supervisa, ...datos.grupos.filter(g => !datos.supervisa.includes(g))]
        .map(g => ({ clave: g, titulo: g, mio: datos.supervisa.includes(g), personas: datos.personas.filter(p => p.grupos.includes(g)) }))
        .filter(x => x.personas.length)
        .concat([{ clave: '-', titulo: t('ot.asignar.sinGrupo'), personas: datos.personas.filter(p => !p.grupos.length) }])
        .filter(x => x.personas.length)
    : [{ clave: 'todos', personas: datos.personas }]

  const soltar = (e, ot) => {
    e.preventDefault()
    setSobre(null)
    const persona = datos.personas.find(p => String(p.id) === e.dataTransfer.getData('text/persona'))
    if (persona) asignar(persona, ot)
  }

  return (
    <div className="space-y-4">
      <Jornada datos={datos} trabajando={trabajando} recibido={recibido} ahora={ahora} t={t} />
      {error && <p className="text-red-500 text-xs">{error}</p>}

      <div className="grid gap-5 lg:grid-cols-[minmax(300px,360px)_1fr] items-start">
        {/* People */}
        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 px-1">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('ot.asignar.personas')} · {t('ot.asignar.nTrabajando', { n: trabajando, total: datos.personas.length })}
            </h3>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
              {VISTAS_PERSONAS.filter(v => v !== 'yo' || datos.yo).map(v => (
                <button key={v} type="button" onClick={() => elegirVista(v)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium ${modo === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                  {t(`ot.asignar.vistaPersonas.${v}`)}
                </button>
              ))}
            </div>
          </div>
          {modo === 'yo' && !yoEsta && <p className="text-sm text-gray-400 px-1">{t('ot.asignar.yoNoAsignable')}</p>}
          {secciones.map(sec => (
            <div key={sec.clave} className="space-y-2">
              {sec.titulo && (
                <h4 className="flex items-center gap-2 px-1 pt-2 text-xs font-semibold text-gray-700">
                  {sec.titulo}
                  {sec.mio && <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">{t('ot.asignar.tuGrupo')}</span>}
                  <span className="font-normal text-gray-400">
                    {t('ot.asignar.nTrabajando', { n: sec.personas.filter(p => p.abierta).length, total: sec.personas.length })}
                  </span>
                </h4>
              )}
          {sec.personas.map(p => (
            <TarjetaPersona key={p.id} p={p} yo={p.id === datos.yo} recibido={recibido} ahora={ahora} t={t} puede={puede}
              reanudable={p.ultima && !p.abierta && enTablero.has(p.ultima.ot_id)}
              onAsignar={() => setEligiendo(e => (e === `${sec.clave}:${p.id}` ? null : `${sec.clave}:${p.id}`))}
              onPausar={() => pausar(p.abierta)}
              onReanudar={() => asignar(p, datos.ots.find(o => o.id === p.ultima.ot_id))}>
              {eligiendo === `${sec.clave}:${p.id}` && (
                <SelectorOt ots={datos.ots} actual={p.abierta?.tipo === 'ot' ? p.abierta.ot_id : null} t={t}
                  onElegir={ot => { setEligiendo(null); asignar(p, ot) }}
                  onClose={() => setEligiendo(null)} />
              )}
            </TarjetaPersona>
          ))}
            </div>
          ))}
          {!datos.personas.length && <p className="text-sm text-gray-400 px-1">{t('ot.asignar.sinPersonas')}</p>}
          {puede && <p className="text-[11px] text-gray-400 px-1 pt-1">{t('ot.asignar.arrastraHint')}</p>}
        </section>

        {/* OTs in execution */}
        <section className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2 px-1">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('ot.asignar.enEjecucion', { n: datos.ots.length })}
            </h3>
            <div className="flex items-center gap-2">
              <select value={grupo} onChange={e => setGrupo(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="">{t('ot.asignar.todosGrupos')}</option>
                {grupos.map(g => <option key={g} value={g}>{g}</option>)}
                <option value="-">{t('ot.asignar.sinGrupo')}</option>
              </select>
              <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder={t('ot.asignar.buscarOt')}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-56 bg-white" />
            </div>
          </div>
          <div className="space-y-2">
            {ots.map(ot => {
              const falta = ot.faltan_planificacion.length > 0
              const vencida = ot.fecha_compromiso && ot.fecha_compromiso < hoy
              return (
                <div key={ot.id}
                  {...(puede && {
                    onDragOver: e => { e.preventDefault(); setSobre(ot.id) },
                    onDragLeave: () => setSobre(s => (s === ot.id ? null : s)),
                    onDrop: e => soltar(e, ot),
                  })}
                  className={`rounded-xl border bg-white px-4 py-3 transition-colors ${
                    sobre === ot.id ? 'border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50/40'
                      : falta ? 'border-amber-200' : ot.trabajando.length ? 'border-emerald-200' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <button type="button" onClick={() => navigate(`/operations/planta/ot?ot=${ot.id}&vista=lista`)}
                      className="text-left min-w-0 group" title={t('ot.asignar.verOt')}>
                      <span className="text-sm font-semibold text-gray-900 group-hover:underline">{ot.codigo}</span>
                      {ot.folio_of && <span className="ml-2 text-xs font-mono text-gray-500">OF {ot.folio_of}</span>}
                      {ot.tipo === 'servicio' ? <>
                        <span className="ml-2 inline-flex px-2 py-0.5 rounded-full bg-violet-50 text-[11px] text-violet-700 align-middle">{ot.categoria}</span>
                        <span className="ml-2 text-sm text-gray-700">{ot.descripcion}</span>
                        {ot.estado === 'planificada' && <span className="ml-2 text-[11px] text-sky-700">{t('ot.asignar.porIniciar')}</span>}
                      </> : <>
                        <span className="ml-2 text-sm font-mono text-gray-600">{ot.articulo_codigo}</span>
                        <span className="ml-2 text-sm text-gray-700">{ot.articulo_descripcion}</span>
                      </>}
                      {ot.cantidad != null && <span className="ml-1 text-xs text-gray-400">× {ot.cantidad}</span>}
                    </button>
                    {puede && (
                      <AgregarPersona t={t}
                        personas={datos.personas.filter(p => !(p.abierta?.tipo === 'ot' && p.abierta.ot_id === ot.id))}
                        onElegir={persona => asignar(persona, ot)} />
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                    {falta ? (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900"
                        title={ot.faltan_planificacion.map(f => t(`ot.faltan.${f}`, { defaultValue: f })).join(', ')}>
                        {t('ot.asignar.faltaPlanificar')}
                      </span>
                    ) : null}
                    {ot.grupo && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{ot.grupo}</span>}
                    {ot.responsable && <span className="text-gray-500">{t('ot.asignar.resp', { nombre: ot.responsable })}</span>}
                    {ot.prioridad && <span className="text-gray-500">· {t(`ot.prioridades.${ot.prioridad}`)}</span>}
                    {ot.fecha_compromiso && (
                      <span className={vencida ? 'text-rose-700 font-medium' : 'text-gray-500'}>
                        · {t(vencida ? 'ot.asignar.vencio' : 'ot.asignar.compromiso', { fecha: formatFecha(ot.fecha_compromiso) })}
                      </span>
                    )}
                  </div>
                  {ot.trabajando.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {ot.trabajando.map(c => (
                        <span key={c.id} className={`inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-0.5 text-xs ${
                          enColacion(c, recibido, ahora) ? 'bg-gray-100 text-gray-600'
                            : c.hora_extra ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900'}`}>
                          <span className="font-medium">{c.persona_nombre}</span>
                          <span className="tabular-nums opacity-75">
                            {enColacion(c, recibido, ahora) ? t('ot.asignar.retomaCorto', { hora: hora(c.inicio) }) : duracion(segundosVivos(c, recibido, ahora))}
                          </span>
                          {c.hora_extra && <span title={t('ot.asignar.autorizadaPor', { por: c.autorizada_por })}>HE</span>}
                          {puede && (
                            <button type="button" onClick={() => pausar(c)} title={t('ot.asignar.pausar')}
                              className="w-4 h-4 rounded-full hover:bg-black/10 leading-none">×</button>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1.5">{t('ot.asignar.nadie')}</p>
                  )}
                </div>
              )
            })}
            {!ots.length && <p className="text-sm text-gray-400 text-center py-10">{t('ot.asignar.sinOts')}</p>}
          </div>
          <p className="text-[11px] text-gray-400 px-1">{t('ot.asignar.soloEjecucion')}</p>
        </section>
      </div>
      {ui}
    </div>
  )
}

function Jornada({ datos, trabajando, recibido, ahora, t }) {
  const j = datos.jornada_hoy
  // Where the clock is now, from the server's time when the data arrived.
  const ya = new Date(Date.parse(datos.ahora) + (ahora - recibido))
  const enCol = j?.colacion_inicio && ya >= new Date(Date.parse(j.colacion_inicio)) && ya < new Date(Date.parse(j.colacion_fin))
  const hoy = new Date(`${String(datos.ahora).slice(0, 10)}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', day: 'numeric', month: 'long' })
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm ${
      datos.en_jornada || enCol ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50'}`}>
      <span className="font-semibold text-gray-900 capitalize">{hoy}</span>
      <span className="text-gray-500">
        {j ? t('ot.asignar.jornada', { inicio: hora(j.inicio), fin: hora(j.fin) }) : t('ot.asignar.noLaboral')}
        {j?.colacion_inicio && ` · ${t('ot.asignar.colacion', { inicio: hora(j.colacion_inicio), fin: hora(j.colacion_fin) })}`}
      </span>
      {enCol
        ? <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">{t('ot.asignar.enColacion')}</span>
        : datos.en_jornada
        ? <span className="inline-flex px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">{t('ot.asignar.enJornada')}</span>
        : <span className="text-xs text-amber-800">{t('ot.asignar.fueraJornada')}</span>}
      {trabajando > 0 && <span className="text-xs text-gray-500">{t('ot.asignar.nTrabajandoCorto', { n: trabajando })}</span>}
    </div>
  )
}

function TarjetaPersona({ p, yo, recibido, ahora, reanudable, puede, onAsignar, onPausar, onReanudar, t, children }) {
  const c = p.abierta
  const vivos = c ? segundosVivos(c, recibido, ahora) : 0
  return (
    <div draggable={puede}
      onDragStart={puede ? e => { e.dataTransfer.setData('text/persona', String(p.id)); e.dataTransfer.effectAllowed = 'move' } : undefined}
      className={`relative rounded-xl border bg-white px-3.5 py-3 ${puede ? 'cursor-grab active:cursor-grabbing' : ''} ${
        c && !enColacion(c, recibido, ahora) ? (c.hora_extra ? 'border-amber-300' : 'border-emerald-300') : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="font-semibold text-gray-900">{p.nombre}</span>
          {yo && <span className="ml-1.5 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">{t('ot.asignar.tu')}</span>}
          {p.cargo && <span className="ml-1.5 text-xs text-gray-400">{p.cargo}</span>}
        </div>
        <span className="text-[11px] text-gray-400 shrink-0" title={t('ot.asignar.hoyTotal')}>
          {t('ot.asignar.hoy')} {duracion(p.segundos_hoy + (c ? vivos - c.segundos : 0))}
        </span>
      </div>
      <div className="mt-1 text-xs">
        {c && enColacion(c, recibido, ahora) ? (
          <p className="text-gray-600">
            ◐ {t('ot.asignar.retoma', { hora: hora(c.inicio), codigo: c.codigo })}
            {c.titulo && <span className="block text-gray-500 truncate">{c.titulo}</span>}
          </p>
        ) : c ? (
          <p className={c.hora_extra ? 'text-amber-800' : 'text-emerald-700'}>
            ● {t(c.tipo === 'parte' ? 'ot.asignar.trabajandoEnParte' : 'ot.asignar.trabajandoEn', { codigo: c.codigo })}
            <span className="ml-1 font-semibold tabular-nums">{duracion(vivos)}</span>
            {c.hora_extra && <span className="ml-1">({t('ot.asignar.horaExtra')})</span>}
            <span className="block text-gray-500 truncate">{c.titulo ? `${c.titulo} · ` : ''}{t('ot.asignar.desde', { hora: hora(c.inicio) })}</span>
          </p>
        ) : p.ultima ? (
          <p className="text-gray-500">{t('ot.asignar.enPausa', { codigo: p.ultima.codigo, hora: hora(p.ultima.fin) })}</p>
        ) : (
          <p className="text-gray-400">{t('ot.asignar.libre')}</p>
        )}
      </div>
      {puede && <div className="flex gap-2 mt-2">
        <button type="button" onClick={onAsignar}
          className="text-xs font-medium bg-gray-900 text-white rounded-md px-2.5 py-1 hover:bg-gray-800">
          {c ? t('ot.asignar.cambiar') : t('ot.asignar.asignar')}
        </button>
        {c?.tipo === 'ot' && (
          <button type="button" onClick={onPausar}
            className="text-xs font-medium border border-gray-200 rounded-md px-2.5 py-1 text-gray-700 hover:bg-gray-50">
            {t('ot.asignar.pausar')}
          </button>
        )}
        {reanudable && (
          <button type="button" onClick={onReanudar}
            className="text-xs font-medium border border-gray-200 rounded-md px-2.5 py-1 text-gray-700 hover:bg-gray-50">
            {t('ot.asignar.reanudar', { codigo: p.ultima.codigo })}
          </button>
        )}
      </div>}
      {children}
    </div>
  )
}

// A person's "Asignar": pick one of the OTs in execution (searchable).
function SelectorOt({ ots, actual, onElegir, onClose, t }) {
  const [q, setQ] = useState('')
  const ref = useRef(null)
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    const esc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc) }
  }, [onClose])
  const b = q.trim().toLowerCase()
  const lista = ots.filter(o => o.id !== actual && (!b || `${o.codigo} ${o.folio_of || ''} ${o.articulo_codigo || ''} ${o.articulo_descripcion || ''}`.toLowerCase().includes(b)))
  return (
    <div ref={ref} className="absolute left-3 right-3 top-full mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-lg p-2">
      <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('ot.asignar.buscarOt')}
        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm mb-1.5" />
      <ul className="max-h-64 overflow-y-auto">
        {lista.map(o => (
          <li key={o.id}>
            <button type="button" onClick={() => onElegir(o)}
              className="w-full text-left px-2 py-1.5 rounded-md hover:bg-gray-100 text-sm">
              <span className="font-semibold">{o.codigo}</span>
              <span className="ml-1.5 font-mono text-gray-600">{o.tipo === 'servicio' ? o.categoria : o.articulo_codigo}</span>
              <span className="ml-1.5 text-gray-600 truncate">{o.tipo === 'servicio' ? o.descripcion : o.articulo_descripcion}</span>
              {o.faltan_planificacion.length > 0 && <span className="ml-1.5 text-[11px] text-amber-700">({t('ot.asignar.faltaPlanificar')})</span>}
              {o.trabajando.length > 0 && <span className="ml-1.5 text-[11px] text-emerald-700">({o.trabajando.length})</span>}
            </button>
          </li>
        ))}
        {!lista.length && <li className="text-xs text-gray-400 px-2 py-2">{t('ot.asignar.sinOts')}</li>}
      </ul>
    </div>
  )
}

// An OT's "+ Persona": pick someone not already on it.
function AgregarPersona({ personas, onElegir, t }) {
  return (
    <select value="" onChange={e => { const p = personas.find(x => String(x.id) === e.target.value); if (p) onElegir(p) }}
      className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white text-gray-600 shrink-0">
      <option value="">{t('ot.asignar.agregarPersona')}</option>
      {personas.map(p => (
        <option key={p.id} value={p.id}>
          {p.nombre}{p.abierta ? ` (${t('ot.asignar.enCodigo', { codigo: p.abierta.codigo })})` : ''}
        </option>
      ))}
    </select>
  )
}
